/**
 * QM oversight UI (gaps AG-06 / AG-07):
 *  - AiDraftBadge: "AI draft" until a supervisor reviewed the score, then "Reviewed".
 *  - AgentSentimentNotice: purpose + audience statement shown wherever agent
 *    sentiment is displayed.
 *  - DisputeScoreControl: agent-facing "Dispute this score" button + reason dialog.
 *  - QmDisputesPanel: supervisor list + resolve panel.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import { Bot, Info, Loader2, MessageSquareWarning, UserCheck } from 'lucide-react'
import { api } from '../../api/client.js'
import { formatDateTime } from '../../utils/formatDate.js'

export function AiDraftBadge({ aiDraft, humanReviewed }: { aiDraft?: boolean | undefined; humanReviewed?: boolean | undefined }): JSX.Element | null {
  if (aiDraft) {
    return (
      <span title="Scored by AI and not yet reviewed by a supervisor"
        className="inline-flex items-center gap-1 rounded-[3px] bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-bold text-[#A9740A] ring-1 ring-inset ring-amber-200">
        <Bot className="h-3 w-3" /> AI draft
      </span>
    )
  }
  if (humanReviewed) {
    return (
      <span title="Reviewed by a supervisor"
        className="inline-flex items-center gap-1 rounded-[3px] bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
        <UserCheck className="h-3 w-3" /> Reviewed
      </span>
    )
  }
  return null
}

export function AgentSentimentNotice(): JSX.Element {
  return (
    <div className="flex items-start gap-2 rounded-[3px] border border-sky-200 bg-sky-50 px-3 py-2 text-[11.5px] leading-relaxed text-sky-900">
      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
      <p>
        <b>About agent sentiment.</b> Sentiment is estimated automatically from call recordings.
        It is used for coaching and quality improvement. It is visible to you and to supervisors
        and quality reviewers with access to Quality Management. If you have questions about how
        it is used, or want to challenge a result, ask your supervisor or your organisation&apos;s
        Quality or Data Protection contact.
      </p>
    </div>
  )
}

interface Dispute {
  id: string
  status: 'OPEN' | 'UPHELD' | 'ADJUSTED' | 'REJECTED'
  reason: string
  resolutionNote: string | null
  createdAt: string
  resolvedAt: string | null
}

export function DisputeScoreControl({ evaluationId }: { evaluationId: string }): JSX.Element {
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    void api.get<{ items: Dispute[] }>(`/qm/evaluations/${evaluationId}/disputes`)
      .then((d) => setDisputes(d.items))
      .catch(() => setDisputes([]))
  }, [evaluationId])
  useEffect(load, [load])

  const current = disputes[0]
  const hasOpen = disputes.some((d) => d.status === 'OPEN')

  const submit = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await api.post(`/qm/evaluations/${evaluationId}/disputes`, { reason })
      setOpen(false)
      setReason('')
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the dispute.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {!hasOpen && (
          <button type="button" onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <MessageSquareWarning className="h-3.5 w-3.5" /> Dispute this score
          </button>
        )}
        {current && (
          <span className="text-xs text-slate-500">
            Dispute {current.status === 'OPEN' ? 'open - awaiting review' : current.status.toLowerCase()} · {formatDateTime(current.createdAt)}
            {current.resolutionNote ? ` · ${current.resolutionNote}` : ''}
          </span>
        )}
      </div>
      {open && (
        <div role="dialog" aria-label="Dispute this score" className="rounded-[3px] border border-slate-200 bg-white p-3 shadow-sm">
          <p className="mb-1 text-sm font-semibold text-slate-800">Dispute this score</p>
          <p className="mb-2 text-xs text-slate-500">Explain why you think the score is wrong. A supervisor will review it; the score is not changed until they do.</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Reason"
            className="block min-h-[80px] w-full rounded-[3px] border border-slate-200 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none" />
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-[3px] px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
            <button type="button" onClick={() => void submit()} disabled={busy || reason.trim().length < 5}
              className="inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit dispute
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

interface DisputeRow extends Dispute {
  evaluationId: string
  callId: string
  formName: string
  percentage: number | null
  raisedByName: string | null
}

export function QmDisputesPanel(): JSX.Element {
  const [items, setItems] = useState<DisputeRow[] | null>(null)
  const [filter, setFilter] = useState<'OPEN' | 'ALL'>('OPEN')
  const [error, setError] = useState<string | null>(null)
  const [resolving, setResolving] = useState<string | null>(null)
  const [status, setStatus] = useState<'UPHELD' | 'ADJUSTED' | 'REJECTED'>('REJECTED')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    void api.get<{ items: DisputeRow[] }>(`/qm/disputes${filter === 'OPEN' ? '?status=OPEN' : ''}`)
      .then((d) => { setItems(d.items); setError(null) })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load disputes.'))
  }, [filter])
  useEffect(load, [load])

  const resolve = async (id: string): Promise<void> => {
    setBusy(true)
    try {
      await api.put(`/qm/disputes/${id}/resolve`, { status, note })
      setResolving(null)
      setNote('')
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resolve the dispute.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-[3px] border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><MessageSquareWarning className="h-4 w-4" /> Score disputes</h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value as 'OPEN' | 'ALL')} aria-label="Dispute filter"
          className="rounded-[3px] border border-slate-200 px-2 py-1 text-xs">
          <option value="OPEN">Open</option>
          <option value="ALL">All</option>
        </select>
      </div>
      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
      {items === null ? (
        <p className="text-xs text-slate-500"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-500">No disputes.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((d) => (
            <li key={d.id} className="py-2.5 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-slate-800">
                  {d.raisedByName ?? 'Agent'} · {d.formName}{d.percentage !== null ? ` · ${Math.round(d.percentage)}%` : ''}
                </span>
                <span className="text-xs text-slate-500">{d.status} · {formatDateTime(d.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-600">{d.reason}</p>
              {d.resolutionNote && <p className="mt-0.5 text-xs italic text-slate-500">Resolution: {d.resolutionNote}</p>}
              {d.status === 'OPEN' && resolving !== d.id && (
                <button type="button" onClick={() => setResolving(d.id)} className="mt-1.5 text-xs font-semibold text-blue-600 hover:underline">Resolve</button>
              )}
              {resolving === d.id && (
                <div className="mt-2 space-y-2">
                  <p className="text-[11px] text-slate-500">To change the score, review the evaluation from the call&apos;s Quality Management tab, then choose Adjusted.</p>
                  <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} aria-label="Outcome"
                    className="rounded-[3px] border border-slate-200 px-2 py-1 text-xs">
                    <option value="UPHELD">Upheld (agent is right)</option>
                    <option value="ADJUSTED">Adjusted (score changed)</option>
                    <option value="REJECTED">Rejected (score stands)</option>
                  </select>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Resolution note" aria-label="Resolution note"
                    className="block min-h-[60px] w-full rounded-[3px] border border-slate-200 px-2.5 py-1.5 text-xs focus:border-blue-500 focus:outline-none" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => void resolve(d.id)} disabled={busy || note.trim() === ''}
                      className="rounded-[3px] bg-blue-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">Save</button>
                    <button type="button" onClick={() => setResolving(null)} className="rounded-[3px] px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
